import { useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Lock, SpinnerGap, CheckCircle, User, ShieldCheck } from "@phosphor-icons/react";
import { useLang } from "../contexts/LanguageContext";
import TwoFactorCard from "../components/TwoFactorCard";
import HintsCard from "../components/HintsCard";

import { API_BASE as API } from "../lib/apiBase";
// Eric 2026-07-25 iter148 — migration drift : label du rôle affiché.
import { canAccessAdmin } from "../utils/permissions";

export default function SettingsPage() {
  const { user } = useAuth();
  const { lang, t } = useLang();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }

    setLoading(true);
    try {
      await axios.put(
        `${API}/api/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        { withCredentials: true }
      );
      toast.success("Mot de passe mis à jour !");
      setSuccess(true);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold tracking-tight font-['Outfit'] mb-6" data-testid="settings-page-title">
        Paramètres
      </h1>

      {/* Profile info */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6 mb-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xl font-bold">
            {user?.name?.charAt(0)?.toUpperCase()}
          </div>
          <div>
            <h2 className="font-semibold text-lg">{user?.name}</h2>
            <p className="text-sm text-zinc-500">{user?.email}</p>
            <span className={`inline-block mt-1 px-2 py-0.5 text-xs font-semibold rounded ${
              canAccessAdmin(user) ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
            }`} data-testid="settings-display-role">
              {user?.display_role || (canAccessAdmin(user) ? "Administrateur" : "Utilisateur")}
            </span>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div className="bg-white border border-zinc-200 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={20} className="text-zinc-600" />
          <h3 className="font-medium">{t("change_password")}</h3>
        </div>

        {success && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-sm flex items-center gap-2 text-blue-700 text-sm" data-testid="password-success">
            <CheckCircle size={18} weight="fill" />
            Mot de passe mis à jour avec succes
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="currentPassword">{t("current_password")}</Label>
            <Input
              id="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Votre mot de passe actuel"
              required
              className="h-11"
              data-testid="current-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="newPassword">{t("new_password")}</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 caracteres"
              required
              minLength={6}
              className="h-11"
              data-testid="new-password-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmez votre nouveau mot de passe"
              required
              minLength={6}
              className="h-11"
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-[#002FA7] hover:bg-[#002280]"
            data-testid="change-password-btn"
          >
            {loading ? (
              <SpinnerGap size={20} className="animate-spin" />
            ) : (
              <>
                <ShieldCheck size={18} className="mr-2" />
                {t("update_password")} le mot de passe
              </>
            )}
          </Button>
        </form>
      </div>

      {/* Vincent #208 (Eric 2026-05-28) — Carte 2FA */}
      <div className="mt-6">
        <TwoFactorCard />
      </div>

      {/* Vincent B598.d (Eric 2026-07-29) — Micro-page astuces (réinitialisables) */}
      <div className="mt-6">
        <HintsCard lang={lang} />
      </div>
    </div>
  );
}
